/**
 * Strings both account surfaces render.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary, no restatement of a mechanic the screen already
 * performs. Every reason this file's wording exists lives in the pack and the
 * PRD-TDD, which is where a reviewer reads it.
 */
export const sharedCopy = {
  crumbs: {
    console: 'لوحة التشغيل',
    users: 'المستخدمون',
  },

  action: {
    cancel: 'ألغِ',
    apply: 'طبّق',
  },

  /**
   * The account roles, worded once. The values are the registry's; the wording
   * is the console's, and a value with no entry renders as itself rather than
   * being dropped — a role the operator cannot name is still a role, and a
   * missing one is a hole.
   */
  roles: {
    user: 'مشترك',
    operator: 'مشغّل',
    admin: 'مدير',
  } as Readonly<Record<string, string>>,

  /**
   * The services a role grant is written to, worded for the operator.
   *
   * Present only so a **partial** write can name what did not land: seven
   * services take the grant on their own route and nothing spans them, so «حُفظ
   * الدور» on a run that failed on two of them would be a claim the console
   * cannot support.
   */
  services: {
    auth: 'الحسابات',
    content: 'المحتوى',
    engagement: 'التفاعل',
    analytics: 'التقارير',
    finance: 'الاشتراك',
    support: 'الحالات',
    notification: 'الإشعارات',
    assets: 'الأصول',
    search: 'البحث',
  } as Readonly<Record<string, string>>,

  accountState: {
    active: 'فاعل',
    blocked: 'محظور',
  },

  /** «تُعرَض ٨ حسابات من ١٢٬٤٨٠.» — stated always, even when the two agree. */
  truncation: {
    prefix: 'تُعرَض',
    middle: 'حسابات من',
  },

  block: {
    /** The dialog names the person; the row menu names the act. */
    menu: 'احظر الحساب',
    unblockMenu: 'ارفع الحظر',
    heading: (name: string) => `حظر حساب «${name}»؟`,
    body: 'ينقطع دخول صاحب الحساب فوراً. استحقاقه وقوائمه وحالاته تبقى كما هي، ويعود كلّ شيء بمجرّد رفع الحظر — الحظر منعُ دخولٍ لا حذفَ حساب.',
    commit: 'احظر الحساب',
    blocked: 'حُظر الحساب.',
    unblocked: 'رُفع الحظر عن الحساب.',
  },
} as const;

/** The role's own name, or the token itself where the console has no word for it. */
export function roleLabel(role: string): string {
  return sharedCopy.roles[role] ?? role;
}

/** The service's own name, or the key itself — a service the console cannot name is still a service. */
export function serviceLabel(service: string): string {
  return sharedCopy.services[service] ?? service;
}
